/**
 * Knopf. Uebernommen aus crm-web, reduziert auf die drei Varianten, die diese
 * Anwendung tatsaechlich benutzt.
 */

import { forwardRef, ReactNode } from 'react';
import { clsx } from 'clsx';
import { Loader2 } from 'lucide-react';

interface ButtonProps {
  variant?: 'primary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md';
  loading?: boolean;
  children?: ReactNode;
  className?: string;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  title?: string;
  /** Fuer den sicheren Knopf in Bestaetigungsdialogen. */
  autoFocus?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      disabled,
      loading,
      children,
      type = 'button',
      onClick,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        onClick={onClick}
        className={clsx(
          'inline-flex items-center justify-center font-semibold rounded-xl transition-colors duration-200',
          'focus:outline-none focus:ring-2 focus:ring-offset-2',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          variant === 'primary' && [
            'bg-primary-600 text-white hover:bg-primary-700',
            'shadow-soft focus:ring-primary-500',
          ],
          variant === 'outline' && [
            'bg-white text-gray-700 border border-gray-200',
            'hover:bg-gray-50 focus:ring-primary-500',
          ],
          variant === 'ghost' && [
            'text-gray-600 hover:bg-gray-100 focus:ring-gray-400',
          ],
          variant === 'danger' && [
            'bg-danger-600 text-white hover:bg-danger-500',
            'shadow-soft focus:ring-danger-500',
          ],
          size === 'sm' && 'px-3 py-1.5 text-sm',
          size === 'md' && 'px-4 py-2.5 text-sm',
          className
        )}
        {...props}
      >
        {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
